import { useCallback, useEffect, useRef, useState } from 'react'

// Load-once viewport detection. Returns [ref, inView]: attach `ref` to a DOM
// node and `inView` flips to true the first time the node nears the viewport,
// then stays true (we stop observing). `rootMargin` prefetches slightly ahead
// so images resolve just before they scroll in. Degrades to always-in-view
// where IntersectionObserver is unavailable (SSR, old browsers).
export default function useInView({ rootMargin = '200px 0px', once = true } = {}) {
    const supported =
        typeof window !== 'undefined' && 'IntersectionObserver' in window
    const [inView, setInView] = useState(!supported)
    const observerRef = useRef(null)

    const ref = useCallback(
        (node) => {
            if (observerRef.current) {
                observerRef.current.disconnect()
                observerRef.current = null
            }
            if (!node || !supported) return

            const observer = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0]
                    if (entry?.isIntersecting) {
                        setInView(true)
                        if (once) {
                            observer.disconnect()
                            observerRef.current = null
                        }
                    } else if (!once) {
                        setInView(false)
                    }
                },
                { rootMargin },
            )
            observer.observe(node)
            observerRef.current = observer
        },
        [supported, rootMargin, once],
    )

    useEffect(
        () => () => {
            if (observerRef.current) observerRef.current.disconnect()
        },
        [],
    )

    return [ref, inView]
}
